import './DocumentGroupName.scss';
import { documentsApi } from 'api/documentsApi';
import { actionCreator } from 'api/utils';
import classNames from 'classnames';
import { Button } from 'components/InteractiveUIControls/Button/Button';
import { limitString } from 'components/Pipes/textPipes';
import { Svgicon } from 'components/Svgicon/Svgicon';
import { ModalActivationContext } from 'contexts';
import { type FilesStore,
  type IndividualFileStore } from 'pages/Firm/FileViewModal/FileViewModal';
import {
  type FC,
  useContext,
} from 'react';
import { useDispatch,
  useSelector } from 'react-redux';
import { isMobile } from 'services/mobile';
import { filesActionTypes } from 'store/actions';
import { type DocumentsStoreState,
  type Filters,
  type FilterStoreState,
  markDocumentsAsSeen,
  type PeriodFilters } from 'store/slices';
import {
  type DocumentDTO,
  getItemsFromStorage,
  type LoginAsFromStorage} from 'types';
import { COLORS } from 'types/Colors';

type DocumentGroupNameProps = {
  categoryId: string,
  documentGroupId: string,
  documentGroupName: string,
  documents: DocumentDTO[],
  isOpen: boolean,
  numberOfDocuments: number,
  numberOfNewDocuments: number,
};

export const DocumentGroupName: FC<DocumentGroupNameProps> = ({
  categoryId,
  documentGroupId,
  documentGroupName,
  documents,
  isOpen,
  numberOfDocuments,
  numberOfNewDocuments,
}) => {
  const dispatch = useDispatch();
  const { toggleDownloadsModal } = useContext(ModalActivationContext);
  const activeFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.activeFilters) as Filters | null;
  const {
    viewingFileByIdLoading,
  } = useSelector<FilesStore>((state) => state.files) as IndividualFileStore;
  const {
    displayType,
  } = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents,
  ) as DocumentsStoreState;
  const periodFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.usualDocumentsFilter) as PeriodFilters | null;

  const {loginAs} = getItemsFromStorage<LoginAsFromStorage>([
    'loginAs',
  ], sessionStorage);

  const getUnseenDocuments = async () => {
    if (!isOpen && !documents && activeFilters) {
      const fetchedDocuments = await documentsApi.getDocuments(
        categoryId as string,
        documentGroupId as string,
        {
          filterByNewDocuments: false,
          firmId: activeFilters.organizationId,
          ...activeFilters,
          displayType: displayType.id,
          ...periodFilters,
        },
        1,
        numberOfDocuments,
      );
      return fetchedDocuments.filter((doc) => !doc.userViewed);
    } else {
      return documents.filter((doc) => !doc.userViewed);
    }
  };

  const markAsSeenClick = async (
    event?: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    event?.stopPropagation();

    dispatch(actionCreator(filesActionTypes.VIEWING_FILE_BY_ID_LOADING, true));

    const filteredNewDocs = await getUnseenDocuments();

    if (filteredNewDocs.length > 0) {
      const documentIds = filteredNewDocs.map((doc) => doc._id);
      await documentsApi.viewDocuments({ documentIds });
      dispatch(markDocumentsAsSeen({ categoryId,
        documentGroupId,
        documentIds }));
    }

    dispatch(actionCreator(filesActionTypes.VIEWING_FILE_BY_ID_LOADING, false));
  };

  const onClickDownload = async (
    event: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
  ) => {
    event?.stopPropagation();
    const fullGroupIdForPeriodType = `${categoryId}/${documentGroupId}`;
    toggleDownloadsModal(true, displayType.id === 'type' ? documentGroupId : fullGroupIdForPeriodType);
  };

  return (
    <div
      className={classNames('nameContainer', {rounded: isOpen})}
    >
      <div className='nameLeft'>
        {
          isOpen ?
            <Svgicon className='size-8' id='opened-folder' /> :
            <Svgicon className='size-8' id='closed-folder' />
        }
        <div className='documentGroupDetails'>
          <p className='m-0 text-font-1 font-semibold'>
            {isMobile() ? limitString(documentGroupName, 24) : documentGroupName}
          </p>
          <p className='m-0 text-font-4 font-medium text-black-500'>
            {`${numberOfDocuments} documents`}
          </p>
        </div>
      </div>
      {
        <div className='nameRight'>
          {Boolean(numberOfNewDocuments) &&
            <div className={classNames('tooltip-wrapper black w-fit', {fileViewButton: !isOpen})}>
              <div className='flex h-[1rem] items-center rounded-large bg-main-700 px-[0.375rem] py-[0.078125rem]'>
                <div className='m-0 text-nowrap text-font-4 text-white-999'>
                  {numberOfNewDocuments} new
                </div>
              </div>
            </div>}
          {Boolean(numberOfNewDocuments) && !loginAs && !isMobile() &&
            <Button
              className={classNames({fileViewButton: !isOpen})}
              data-test='mark-file-as-seen'
              icon='only'
              iconColor={COLORS['custom-blue']}
              iconHoveredColor={COLORS['$main-500']}
              iconid='mark-seen'
              isLoading={viewingFileByIdLoading}
              onClick={(event) => markAsSeenClick(event)}
              size='medium'
              tooltip='Mark as seen'
              type='transparent'
            />}
          {!isMobile() || isOpen ?
            <Button
              className={classNames({fileViewButton: !isOpen})}
              data-test='download-file'
              icon='only'
              iconColor={COLORS['custom-blue']}
              iconHoveredColor={COLORS['$main-500']}
              iconid='download-arrow-1f'
              onClick={(event) => onClickDownload(event)}
              size='medium'
              tooltip='Download'
              type='transparent'
            /> : null}
        </div>
      }
    </div>
  );
};
